// Admin v2 — Ranglista & pontrendszer (Figma ADM_13, 13b–13e).
// Konfiguráció (ranglisták, pontszabályok, achievementek, szezonok): app_settings/leaderboard.
// Előnézet: Minden idők = users.dqScore (CP); napi/heti/havi = cp_transactions összege az időszakban.
import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, limit, onSnapshot, query, setDoc, Timestamp, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { Icon } from '../icons';
import { fmtDateDot, fmtInt, toDate } from '../data';
import {
  Avatar, Badge, Button, Card, ConfirmModal, Empty, Field, IconTile, Input, Modal, PageHeader, SectionTitle, Segmented, Select, Tabs, TextArea, Toggle, Tone, useToast,
} from '../ui';

type Tab = 'boards' | 'rules' | 'achievements' | 'seasons';
interface Board { id: string; name: string; type: 'global' | 'regional' | 'venue'; category: string; period: string; top: number; region: string; enabled: boolean }
interface Rule { id: string; event: string; description: string; points: number; multiplier: number; enabled: boolean }
interface Ach { id: string; name: string; requirement: string; reward: number; rarity: 'common' | 'rare' | 'epic' | 'legendary'; icon: string; enabled: boolean }
interface Season { id: string; name: string; start: string; end: string; rewards: string[]; status: 'active' | 'scheduled' | 'archived' }
interface Cfg { boards: Board[]; rules: Rule[]; achievements: Ach[]; seasons: Season[] }

const REF = () => doc(db, 'app_settings', 'leaderboard');
const TYPE_LABEL = { global: 'Globális', regional: 'Regionális', venue: 'Helyszín' };
const TYPE_ICON = { global: 'Trophy', regional: 'MapPin', venue: 'Buildings' } as const;
const TYPE_TONE: Record<Board['type'], Tone> = { global: 'gold', regional: 'cyan', venue: 'purple' };
const RARITY: Record<Ach['rarity'], { t: string; tone: Tone }> = { common: { t: 'Gyakori', tone: 'gray' }, rare: { t: 'Ritka', tone: 'cyan' }, epic: { t: 'Epikus', tone: 'purple' }, legendary: { t: 'Legendás', tone: 'gold' } };
const S_STATUS: Record<Season['status'], { t: string; tone: Tone }> = { active: { t: 'Aktív', tone: 'green' }, scheduled: { t: 'Ütemezett', tone: 'cyan' }, archived: { t: 'Archivált', tone: 'gray' } };
const uid = (p: string) => `${p}_${Date.now().toString(36)}`;

export default function LeaderboardPage() {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('boards');
  const [cfg, setCfg] = useState<Cfg>({ boards: [], rules: [], achievements: [], seasons: [] });
  const [edit, setEdit] = useState<{ kind: Tab; item: Board | Rule | Ach | Season | null } | null>(null);
  const [del, setDel] = useState<{ kind: Tab; id: string; name: string } | null>(null);

  useEffect(() => onSnapshot(REF(), (s) => {
    const v = s.data() ?? {};
    setCfg({ boards: v.boards ?? [], rules: v.rules ?? [], achievements: v.achievements ?? [], seasons: v.seasons ?? [] });
  }, () => undefined), []);

  const save = async (kind: Tab, list: unknown[]) => {
    const key = kind === 'boards' ? 'boards' : kind === 'rules' ? 'rules' : kind === 'achievements' ? 'achievements' : 'seasons';
    await setDoc(REF(), { [key]: list }, { merge: true });
  };
  const upsert = async <T extends { id: string }>(kind: Tab, list: T[], item: T) => {
    try { await save(kind, list.some((x) => x.id === item.id) ? list.map((x) => (x.id === item.id ? item : x)) : [...list, item]); toast('Mentve.'); }
    catch { toast('A mentés nem sikerült.', 'red'); }
  };
  const toggle = async <T extends { id: string; enabled: boolean }>(kind: Tab, list: T[], id: string) =>
    save(kind, list.map((x) => (x.id === id ? { ...x, enabled: !x.enabled } : x))).catch(() => toast('A mentés nem sikerült.', 'red'));

  const newLabel = { boards: 'Új ranglista', rules: 'Új pontszabály', achievements: 'Új achievement', seasons: 'Új szezon' }[tab];
  const IconBtns = ({ onEdit, onDel }: { onEdit: () => void; onDel: () => void }) => (
    <div className="flex gap-2 shrink-0">
      <button type="button" onClick={onEdit} aria-label="Szerkesztés" className="inline-flex items-center justify-center w-[32px] h-[32px] rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#B8B8C3] hover:text-white"><Icon name="PencilSimpleLine" size={15} /></button>
      <button type="button" onClick={onDel} aria-label="Törlés" className="inline-flex items-center justify-center w-[32px] h-[32px] rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#ef4444]"><Icon name="Trash" size={15} /></button>
    </div>
  );
  const EnBadge = ({ on, onClick }: { on: boolean; onClick: () => void }) => (
    <button type="button" onClick={onClick} title="Kattints a váltáshoz"><Badge tone={on ? 'green' : 'gray'}>{on ? 'Engedélyezve' : 'Letiltva'}</Badge></button>
  );

  return (
    <div>
      <PageHeader title="Ranglista & pontrendszer" sub="Ranglisták, pontszabályok, achievementek és szezonok"
        right={<Button icon="Plus" onClick={() => setEdit({ kind: tab, item: null })}>{newLabel}</Button>} />
      <Tabs<Tab> value={tab} onChange={setTab} options={[{ value: 'boards', label: 'Ranglisták' }, { value: 'rules', label: 'Pontozási szabályok' }, { value: 'achievements', label: 'Achievementek' }, { value: 'seasons', label: 'Szezonok' }]} />

      {tab === 'boards' && (
        <>
          {cfg.boards.length === 0 ? <Card><Empty>Még nincs ranglista. Az „Új ranglista” gombbal vehetsz fel.</Empty></Card> : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {cfg.boards.map((b) => (
                <Card key={b.id} className="!p-4 flex items-start gap-3">
                  <IconTile icon={TYPE_ICON[b.type]} tone={TYPE_TONE[b.type]} size={40} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-white">{b.name}</div>
                    <div className="text-[12px] text-[#8a8f99] truncate">Típus: {TYPE_LABEL[b.type]}{b.region ? ` · ${b.region}` : ''} · Kategória: {b.category} · {b.period} · Top {b.top}</div>
                    <div className="mt-2"><EnBadge on={b.enabled} onClick={() => toggle('boards', cfg.boards, b.id)} /></div>
                  </div>
                  <IconBtns onEdit={() => setEdit({ kind: 'boards', item: b })} onDel={() => setDel({ kind: 'boards', id: b.id, name: b.name })} />
                </Card>
              ))}
            </div>
          )}
          <Preview />
        </>
      )}

      {tab === 'rules' && (
        <>
          <h2 className="mb-4 text-[15px] font-extrabold italic uppercase text-white">Pontszabály kezelés</h2>
          <div className="bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden overflow-x-auto">
            <table className="w-full min-w-[760px] text-left">
              <thead><tr className="bg-[#1d232a] text-[12px] uppercase text-[#8a8f99]">
                <th className="pl-5 pr-4 h-[39px] font-semibold">Esemény típus</th><th className="px-4 font-semibold">Leírás</th><th className="px-4 font-semibold">Pontérték</th><th className="px-4 font-semibold">Szorzó</th><th className="px-4 font-semibold">Állapot</th><th className="px-4" />
              </tr></thead>
              <tbody>
                {cfg.rules.map((r) => (
                  <tr key={r.id} className="border-t border-[#31383D] h-[52px] text-[13px]">
                    <td className="pl-5 pr-4 font-semibold text-white">{r.event}</td>
                    <td className="px-4 text-[#B8B8C3]">{r.description}</td>
                    <td className="px-4 text-[#B8B8C3]">{r.points >= 0 ? '+' : ''}{r.points} pont</td>
                    <td className="px-4 text-[#B8B8C3]">×{r.multiplier.toFixed(1).replace('.', ',')}</td>
                    <td className="px-4"><EnBadge on={r.enabled} onClick={() => toggle('rules', cfg.rules, r.id)} /></td>
                    <td className="px-4"><IconBtns onEdit={() => setEdit({ kind: 'rules', item: r })} onDel={() => setDel({ kind: 'rules', id: r.id, name: r.event })} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {cfg.rules.length === 0 && <Empty>Még nincs pontszabály.</Empty>}
          </div>
          <p className="mt-3 text-[12px] text-[#8a8f99]">A rangsorolt meccsek CP-jét a szerveroldali CP-motor számolja; ezek a szabályok a ranglista-pontokra vonatkoznak.</p>
        </>
      )}

      {tab === 'achievements' && (cfg.achievements.length === 0 ? <Card><Empty>Még nincs achievement.</Empty></Card> : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {cfg.achievements.map((a) => (
            <Card key={a.id} className="!p-4 flex items-start gap-3">
              <IconTile icon={a.icon || 'Trophy'} tone={RARITY[a.rarity].tone === 'gray' ? 'gold' : RARITY[a.rarity].tone} size={40} />
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-white">{a.name}</div>
                <div className="text-[12px] text-[#8a8f99]">Követelmény: {a.requirement} · Jutalom: {fmtInt(a.reward)} pont</div>
                <div className="flex gap-2 mt-2"><Badge tone={RARITY[a.rarity].tone}>{RARITY[a.rarity].t}</Badge><EnBadge on={a.enabled} onClick={() => toggle('achievements', cfg.achievements, a.id)} /></div>
              </div>
              <IconBtns onEdit={() => setEdit({ kind: 'achievements', item: a })} onDel={() => setDel({ kind: 'achievements', id: a.id, name: a.name })} />
            </Card>
          ))}
        </div>
      ))}

      {tab === 'seasons' && (cfg.seasons.length === 0 ? <Card><Empty>Még nincs szezon.</Empty></Card> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {cfg.seasons.map((s) => (
            <Card key={s.id} className="!p-5">
              <div className="flex items-center justify-between gap-2"><span className="text-[15px] font-bold text-white">{s.name}</span><Badge tone={S_STATUS[s.status].tone}>{S_STATUS[s.status].t}</Badge></div>
              <div className="text-[12px] text-[#8a8f99] mt-1 pb-3 border-b border-[#31383D]">{fmtDateDot(toDate(s.start))} – {fmtDateDot(toDate(s.end))}</div>
              <h4 className="mt-3 mb-2 text-[13px] font-extrabold italic uppercase text-white">Jutalmak</h4>
              {s.rewards.map((r, i) => <div key={i} className="flex items-center gap-2 py-1 text-[13px] text-[#B8B8C3]"><Icon name="Trophy" size={15} className={i === 0 ? 'text-[#d9a514]' : i === 1 ? 'text-[#B8B8C3]' : 'text-[#c47a2b]'} />{r}</div>)}
              <div className="flex gap-2 mt-3">
                <Button kind="ghost" icon="PencilSimpleLine" className="!h-[32px] !text-[13px]" onClick={() => setEdit({ kind: 'seasons', item: s })}>Szerkesztés</Button>
                <button type="button" onClick={() => setDel({ kind: 'seasons', id: s.id, name: s.name })} className="inline-flex items-center gap-1.5 h-[32px] px-3 rounded-full border border-[#ef4444] text-[#ef4444] text-[13px] font-semibold hover:bg-[#ef4444]/10"><Icon name="Trash" size={14} />Törlés</button>
              </div>
            </Card>
          ))}
        </div>
      ))}

      {edit?.kind === 'boards' && <BoardModal b={edit.item as Board | null} onClose={() => setEdit(null)} onSave={(x) => upsert('boards', cfg.boards, x)} />}
      {edit?.kind === 'rules' && <RuleModal r={edit.item as Rule | null} onClose={() => setEdit(null)} onSave={(x) => upsert('rules', cfg.rules, x)} />}
      {edit?.kind === 'achievements' && <AchModal a={edit.item as Ach | null} onClose={() => setEdit(null)} onSave={(x) => upsert('achievements', cfg.achievements, x)} />}
      {edit?.kind === 'seasons' && <SeasonModal s={edit.item as Season | null} onClose={() => setEdit(null)} onSave={async (x) => {
        // aktiválás lezárja az előző aktív szezont
        const list = x.status === 'active' ? cfg.seasons.map((y) => (y.status === 'active' && y.id !== x.id ? { ...y, status: 'archived' as const } : y)) : cfg.seasons;
        await upsert('seasons', list, x);
      }} />}
      {del && (
        <ConfirmModal title="Törlés" danger confirmLabel="Törlés" onClose={() => setDel(null)} text={<>Biztosan törlöd: <b className="text-white">{del.name}</b>?</>}
          onConfirm={async () => {
            const list = (cfg[del.kind as keyof Cfg] as { id: string }[]).filter((x) => x.id !== del.id);
            try { await save(del.kind, list); toast('Törölve.'); } catch { toast('A törlés nem sikerült.', 'red'); }
            setDel(null);
          }} />
      )}
    </div>
  );
}

/* ---------- élő előnézet ---------- */
function Preview() {
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'all'>('all');
  const [users, setUsers] = useState<Record<string, { name: string; avatar: string | null; cp: number }>>({});
  const [gains, setGains] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    getDocs(collection(db, 'users')).then((s) => {
      const m: Record<string, { name: string; avatar: string | null; cp: number }> = {};
      s.docs.forEach((d) => { const v = d.data(); m[d.id] = { name: v.username || v.displayName || 'Játékos', avatar: v.avatarUrl || v.photoURL || null, cp: Number(v.dqScore ?? 0) }; });
      setUsers(m);
    }).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (period === 'all') { setGains(null); return; }
    const days = period === 'day' ? 1 : period === 'week' ? 7 : 30;
    getDocs(query(collection(db, 'cp_transactions'), where('timestamp', '>=', Timestamp.fromDate(new Date(Date.now() - days * 864e5))), limit(5000)))
      .then((s) => { const g: Record<string, number> = {}; s.docs.forEach((d) => { const v = d.data(); g[v.userId] = (g[v.userId] ?? 0) + Number(v.amount ?? 0); }); setGains(g); })
      .catch(() => setGains({}));
  }, [period]);
  const rows = useMemo(() => {
    const list = gains ? Object.entries(gains).map(([id, v]) => ({ id, v })) : Object.entries(users).map(([id, u]) => ({ id, v: u.cp }));
    return list.filter((x) => x.v > 0).sort((a, b) => b.v - a.v).slice(0, 10);
  }, [users, gains]);
  return (
    <Card className="!p-5 mt-6">
      <SectionTitle right={<Segmented size="sm" value={period} onChange={setPeriod} options={[{ value: 'day', label: 'Napi' }, { value: 'week', label: 'Heti' }, { value: 'month', label: 'Havi' }, { value: 'all', label: 'Minden idők' }]} />}>
        Előnézet – Globális top játékosok
      </SectionTitle>
      {rows.length === 0 ? <Empty>Nincs adat ebben az időszakban.</Empty> : rows.map((r, i) => {
        const u = users[r.id];
        const lvl = Math.floor(Math.max(0, u?.cp ?? 0) / 50) + 1;
        return (
          <div key={r.id} className={`flex items-center gap-3 h-[50px] ${i < rows.length - 1 ? 'border-b border-[#31383D]' : ''}`}>
            <span className={`w-8 text-[14px] font-bold ${i === 0 ? 'text-[#d9a514]' : i === 2 ? 'text-[#c47a2b]' : 'text-[#8a8f99]'}`}>#{i + 1}</span>
            <Avatar src={u?.avatar} name={u?.name} size={30} />
            <span className="flex-1 text-[14px] text-white truncate">{u?.name ?? '…'}</span>
            <Badge tone="cyan">CP{lvl}</Badge>
            <span className="w-[110px] text-right text-[14px] font-bold text-[#2bc48a]">{gains ? (r.v > 0 ? '+' : '') : ''}{fmtInt(r.v)} {gains ? 'CP' : 'pont'}</span>
          </div>
        );
      })}
    </Card>
  );
}

/* ---------- modálok ---------- */
function BoardModal({ b, onClose, onSave }: { b: Board | null; onClose: () => void; onSave: (b: Board) => Promise<void> }) {
  const [f, setF] = useState<Board>(b ?? { id: uid('lb'), name: '', type: 'global', category: 'CP', period: 'Havi', top: 50, region: '', enabled: true });
  return (
    <Modal title={b ? 'Ranglista szerkesztése' : 'Új ranglista'} onClose={onClose} width={520}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button disabled={!f.name.trim()} onClick={async () => { await onSave({ ...f, name: f.name.trim() }); onClose(); }}>Mentés</Button></>}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
        <Field label="Név" className="col-span-2"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="pl. Budapest havi ranglista" /></Field>
        <Field label="Típus"><Select value={f.type} onChange={(v) => setF({ ...f, type: v as Board['type'] })} options={[{ value: 'global', label: 'Globális' }, { value: 'regional', label: 'Regionális' }, { value: 'venue', label: 'Helyszín' }]} /></Field>
        <Field label="Régió / helyszín"><Input value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} placeholder="pl. Budapest" /></Field>
        <Field label="Kategória"><Select value={f.category} onChange={(v) => setF({ ...f, category: v })} options={['CP', 'Átlag', '180-ak', 'Győzelmek', 'XP'].map((x) => ({ value: x, label: x }))} /></Field>
        <Field label="Időkeret"><Select value={f.period} onChange={(v) => setF({ ...f, period: v })} options={['Napi', 'Heti', 'Havi', 'Minden idők'].map((x) => ({ value: x, label: x }))} /></Field>
        <Field label="Megjelenített helyezések"><Input inputMode="numeric" value={String(f.top)} onChange={(e) => setF({ ...f, top: Number(e.target.value.replace(/[^\d]/g, '')) || 10 })} /></Field>
        <div className="flex items-end"><div className="flex items-center justify-between w-full px-4 h-[44px] rounded-xl bg-[#1d232a]"><span className="text-[13px] text-white">Engedélyezve</span><Toggle checked={f.enabled} onChange={(v) => setF({ ...f, enabled: v })} /></div></div>
      </div>
    </Modal>
  );
}

function RuleModal({ r, onClose, onSave }: { r: Rule | null; onClose: () => void; onSave: (r: Rule) => Promise<void> }) {
  const [f, setF] = useState<Rule>(r ?? { id: uid('rule'), event: '', description: '', points: 10, multiplier: 1, enabled: true });
  const [mult, setMult] = useState(String(f.multiplier).replace('.', ','));
  return (
    <Modal title={r ? 'Pontszabály szerkesztése' : 'Új pontszabály'} onClose={onClose} width={480}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button disabled={!f.event.trim()} onClick={async () => { await onSave({ ...f, event: f.event.trim(), multiplier: Number(mult.replace(',', '.')) || 1 }); onClose(); }}>Mentés</Button></>}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
        <Field label="Esemény típus" className="col-span-2"><Input value={f.event} onChange={(e) => setF({ ...f, event: e.target.value })} placeholder="pl. Mérkőzés győzelem" /></Field>
        <Field label="Leírás" className="col-span-2"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <Field label="Pontérték"><Input inputMode="numeric" value={String(f.points)} onChange={(e) => setF({ ...f, points: Number(e.target.value.replace(/[^\d-]/g, '')) || 0 })} /></Field>
        <Field label="Szorzó"><Input value={mult} onChange={(e) => setMult(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

function AchModal({ a, onClose, onSave }: { a: Ach | null; onClose: () => void; onSave: (a: Ach) => Promise<void> }) {
  const [f, setF] = useState<Ach>(a ?? { id: uid('ach'), name: '', requirement: '', reward: 50, rarity: 'common', icon: 'Trophy', enabled: true });
  return (
    <Modal title={a ? 'Achievement szerkesztése' : 'Új achievement'} onClose={onClose} width={500}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button disabled={!f.name.trim()} onClick={async () => { await onSave({ ...f, name: f.name.trim() }); onClose(); }}>Mentés</Button></>}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
        <Field label="Név" className="col-span-2"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="pl. Kiszálló mester" /></Field>
        <Field label="Követelmény" className="col-span-2"><Input value={f.requirement} onChange={(e) => setF({ ...f, requirement: e.target.value })} placeholder="pl. checkout100 ≥ 100" /></Field>
        <Field label="Jutalom (pont)"><Input inputMode="numeric" value={String(f.reward)} onChange={(e) => setF({ ...f, reward: Number(e.target.value.replace(/[^\d]/g, '')) || 0 })} /></Field>
        <Field label="Ritkaság"><Select value={f.rarity} onChange={(v) => setF({ ...f, rarity: v as Ach['rarity'] })} options={Object.entries(RARITY).map(([k, v]) => ({ value: k, label: v.t }))} /></Field>
        <Field label="Ikon" className="col-span-2"><Select value={f.icon} onChange={(v) => setF({ ...f, icon: v })} options={['Trophy', 'Target', 'Lightning', 'Medal', 'UsersThree', 'ShieldStar', 'Sparkle'].map((x) => ({ value: x, label: x }))} /></Field>
      </div>
    </Modal>
  );
}

function SeasonModal({ s, onClose, onSave }: { s: Season | null; onClose: () => void; onSave: (s: Season) => Promise<void> }) {
  const [f, setF] = useState<Season>(s ?? { id: uid('season'), name: '', start: '', end: '', rewards: ['Arany trófea', 'Ezüst trófea', 'Bronz trófea'], status: 'scheduled' });
  const [rw, setRw] = useState(f.rewards.map((r, i) => `${i + 1}: ${r}`).join('\n'));
  const [activate, setActivate] = useState(f.status === 'active');
  return (
    <Modal title={s ? 'Szezon szerkesztése' : 'Új szezon'} onClose={onClose} width={520}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button disabled={!f.name.trim() || !f.start || !f.end} onClick={async () => {
        const rewards = rw.split('\n').map((l) => l.replace(/^\s*\d+\s*[:.)-]\s*/, '').trim()).filter(Boolean);
        const status: Season['status'] = activate ? 'active' : new Date(f.end) < new Date() ? 'archived' : 'scheduled';
        await onSave({ ...f, name: f.name.trim(), rewards, status }); onClose();
      }}>Mentés</Button></>}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
        <Field label="Szezon neve" className="col-span-2"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="pl. Tavaszi bajnokság 2027" /></Field>
        <Field label="Kezdő dátum"><Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="[color-scheme:dark]" /></Field>
        <Field label="Záró dátum"><Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="[color-scheme:dark]" /></Field>
        <div className="col-span-2">
          <Field label="Jutalmak"><TextArea rows={3} value={rw} onChange={(e) => setRw(e.target.value)} /></Field>
          <div className="mt-1.5 text-[11px] text-[#8a8f99]">Formátum: helyezés: jutalom (soronként egy)</div>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-4 px-4 py-3 rounded-xl bg-[#1d232a]">
        <div><div className="text-[14px] font-semibold text-white">Szezon aktiválása</div><div className="text-[12px] text-[#8a8f99]">Az aktiválás lezárja az előző szezont</div></div>
        <Toggle checked={activate} onChange={setActivate} />
      </div>
    </Modal>
  );
}
